/* eslint-disable */
// Generated from voia-travel-mobile app/utils/itineraryRouteLines.ts (types stripped). Keep in sync: edit there, re-run the transpile.
/**
 * Route-line geometry for itinerary maps: the stay → stay spine and the
 * day-visit lines around it. Pure (no Mapbox imports) so MapboxItineraries
 * (feed posts, view-trip) and the full-screen ItineraryGlobe draw the same
 * route.
 */
const hasCoords = (p) => typeof p.latitude === 'number' && typeof p.longitude === 'number';
const time = (d) => new Date(d).getTime();
// Sort key for chaining: by check-in day first, then createdAt.
// Multi-night transits split day-visits across days, so day order
// dominates; same-day dvs (e.g. multiple visits on a single
// transition day) tiebreak by creation timestamp. The *1e6 keeps day
// ordering dominant over millis (~31 years overflow, safe for any
// realistic trip).
const chainSortKey = (dv) => {
    const day = new Date(dv.checkInDate).getTime();
    const created = dv.createdAt ? new Date(dv.createdAt).getTime() : 0;
    return (isNaN(day) ? 0 : day) * 1e6 + (isNaN(created) ? 0 : created);
};
const segment = (from, to, lineStyle) => ({
    type: 'Feature',
    geometry: {
        type: 'LineString',
        coordinates: [
            [from.longitude, from.latitude],
            [to.longitude, to.latitude],
        ],
    },
    properties: { lineStyle, fromId: from.id, toId: to.id },
});
export function buildRouteLines(pins, dayVisitAssociations, { isCovered, isOnJourney } = {}) {
    const validPins = pins.filter(hasCoords).sort((a, b) => time(a.checkInDate) - time(b.checkInDate));
    // Transit pins are excluded from the stays so they don't act as waypoints:
    // connections jump directly stay → stay, skipping in-transit nights.
    const destinationPins = validPins.filter((p) => p.type !== 'dayVisit' && p.type !== 'transit');
    const dayVisitPins = validPins.filter((p) => p.type === 'dayVisit');
    // Spine (stays + transits, no day-visits) sorted by check-in. Used for
    // walking back/forward from a day-visit's parent — skipping over any
    // intervening transit pins — to find the prev/next stays that anchor
    // the dv on the map.
    const sortedSpinePins = pins
        .filter((p) => p.type !== 'dayVisit')
        .sort((a, b) => time(a.checkInDate) - time(b.checkInDate));
    // For a day-visit, find: the parent pin (stay OR transit) plus the
    // nearest stays preceding/following the parent that have coordinates.
    const getStayContext = (parentId) => {
        const none = { parent: null, prevStay: null, nextStay: null };
        if (!parentId)
            return none;
        const parentIdx = sortedSpinePins.findIndex((p) => p.id === parentId);
        if (parentIdx === -1)
            return none;
        let prevStay = null;
        for (let i = parentIdx - 1; i >= 0; i--) {
            const p = sortedSpinePins[i];
            if (p.type !== 'transit' && hasCoords(p)) {
                prevStay = p;
                break;
            }
        }
        let nextStay = null;
        for (let i = parentIdx + 1; i < sortedSpinePins.length; i++) {
            const p = sortedSpinePins[i];
            if (p.type !== 'transit' && hasCoords(p)) {
                nextStay = p;
                break;
            }
        }
        return { parent: sortedSpinePins[parentIdx], prevStay, nextStay };
    };
    const chains = new Map();
    const standalones = [];
    const bridgedPairs = new Set();
    const pushChain = (key, prev, next, dv) => {
        const group = chains.get(key) || { prev, next, dvs: [] };
        group.dvs.push(dv);
        chains.set(key, group);
        if (prev && next)
            bridgedPairs.add(`${prev.id}__${next.id}`);
    };
    if (dayVisitAssociations.size > 0) {
        dayVisitPins.forEach((dv) => {
            var _a, _b;
            const { parent, prevStay, nextStay } = getStayContext(dayVisitAssociations.get(dv.id));
            if (!parent)
                return;
            if (parent.type === 'transit') {
                // Trip-start/end transits drop the missing endpoint.
                if (!prevStay && !nextStay)
                    return;
                pushChain(`${(_a = prevStay === null || prevStay === void 0 ? void 0 : prevStay.id) !== null && _a !== void 0 ? _a : 'start'}__${(_b = nextStay === null || nextStay === void 0 ? void 0 : nextStay.id) !== null && _b !== void 0 ? _b : 'end'}`, prevStay, nextStay, dv);
                return;
            }
            if (!hasCoords(parent) || (isOnJourney === null || isOnJourney === void 0 ? void 0 : isOnJourney(dv.id)))
                return;
            standalones.push({ from: parent, to: dv });
        });
    }
    // Connect every consecutive pair of stays. The canonical model has full
    // pin coverage, so any gap between adjacent stays is in-transit nights
    // and still gets a line. Skip when a commute covers the pair, or a
    // bridging day-visit chain replaces it.
    const routeLines = [];
    for (let i = 0; i < destinationPins.length - 1; i++) {
        const current = destinationPins[i];
        const next = destinationPins[i + 1];
        if (isCovered === null || isCovered === void 0 ? void 0 : isCovered(current, next, 'route'))
            continue;
        if (bridgedPairs.has(`${current.id}__${next.id}`))
            continue;
        routeLines.push(segment(current, next, 'solid'));
    }
    const dayVisitLinesSolid = [];
    chains.forEach((group) => {
        const points = [
            ...(group.prev ? [group.prev] : []),
            ...[...group.dvs].sort((a, b) => chainSortKey(a) - chainSortKey(b)),
            ...(group.next ? [group.next] : []),
        ];
        for (let i = 0; i < points.length - 1; i++) {
            if (isCovered === null || isCovered === void 0 ? void 0 : isCovered(points[i], points[i + 1], 'chain'))
                continue;
            dayVisitLinesSolid.push(segment(points[i], points[i + 1], 'solid'));
        }
    });
    const dayVisitLinesDashed = standalones.map((s) => segment(s.from, s.to, 'dashed'));
    return { routeLines, dayVisitLinesSolid, dayVisitLinesDashed };
}
